import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { sourceWithUtm } from '../components/BookingForm';
import { FaCheckCircle, FaChevronLeft, FaChevronRight, FaRegClock } from 'react-icons/fa';

// Page publique de prise de rendez-vous (façon Calendly). Le client choisit un jour
// puis un créneau ; le système assigne automatiquement un membre du pôle (round-robin).
// slug par défaut = "demo".
// Le rendez-vous « demo » (conseiller) se prend dans le formulaire complet de /app,
// en gardant les paramètres de la pub (UTM). Les autres types restent sur cette page.
export default function BookingPage() {
  const { slug: slugParam } = useParams();
  const location = useLocation();
  if (!slugParam || slugParam === 'demo') {
    return <Navigate to={`/app${location.search}#rendez-vous`} replace />;
  }
  return <BookingTypePage slug={slugParam} />;
}

function BookingTypePage({ slug }) {
  const apiBase = process.env.REACT_APP_API_ENDPOINT;
  const clientTz = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return '';
    }
  }, []);

  const [type, setType] = useState(null); // { name, description, durationMin }
  const [slots, setSlots] = useState([]); // [{ startUtc, free }]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDay, setSelectedDay] = useState(null); // clé "YYYY-MM-DD" (local)
  const [selectedSlot, setSelectedSlot] = useState(null); // startUtc (ms)
  const [form, setForm] = useState({ name: '', businessName: '', phone: '', email: '', city: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const loadSlots = async () => {
    setLoading(true);
    setError('');
    try {
      // Type (nom + description) : optionnel, ne bloque pas si absent.
      fetch(`${apiBase}/v1/public/booking/types`)
        .then((r) => r.json())
        .then((list) => {
          if (Array.isArray(list)) {
            const t = list.find((x) => x.slug === slug);
            if (t) setType(t);
          }
        })
        .catch(() => {});

      const res = await fetch(`${apiBase}/v1/public/booking/${encodeURIComponent(slug)}/slots`);
      if (!res.ok) throw new Error('Ce type de rendez-vous est indisponible.');
      const data = await res.json();
      setType((prev) => prev || { name: data.name, durationMin: data.durationMin });
      setSlots(Array.isArray(data.slots) ? data.slots : []);
    } catch (e) {
      setError(e.message || 'Erreur de chargement.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Regroupe les créneaux par jour local.
  const dayKeys = useMemo(() => {
    const map = {};
    for (const s of slots) {
      const d = new Date(s.startUtc);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (!map[key]) map[key] = [];
      map[key].push(s);
    }
    return map;
  }, [slots]);
  const sortedDays = useMemo(() => Object.keys(dayKeys).sort(), [dayKeys]);

  useEffect(() => {
    if (!selectedDay && sortedDays.length) setSelectedDay(sortedDays[0]);
  }, [sortedDays, selectedDay]);

  const daySlots = selectedDay ? dayKeys[selectedDay] || [] : [];

  const fmtDayLabel = (key) => {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  };
  const fmtTime = (ms) =>
    new Date(ms).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  const submit = async () => {
    setError('');
    if (!form.name.trim()) {
      setError('Votre nom est requis.');
      return;
    }
    if (!form.phone.trim() && !form.email.trim()) {
      setError('Un téléphone ou un email est requis.');
      return;
    }
    if (!selectedSlot) {
      setError('Choisissez un créneau.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${apiBase}/v1/public/booking/${encodeURIComponent(slug)}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          slotStartUtc: selectedSlot,
          clientTimezone: clientTz,
          source: sourceWithUtm('vitrine'),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data?.message || '';
        if (msg.startsWith('SLOT_TAKEN')) {
          setError('Ce créneau vient d\'être pris. Choisissez-en un autre.');
          setSelectedSlot(null);
          await loadSlots();
          return;
        }
        if (msg.startsWith('SLOT_INVALID')) {
          setError('Créneau invalide. Rechargez la page.');
          return;
        }
        throw new Error('Réservation impossible. Réessayez.');
      }
      setDone(true);
    } catch (e) {
      setError(e.message || 'Erreur.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-6">
          <span className="text-3xl font-extrabold">
            <span style={{ color: '#113039' }}>Oui</span>
            <span style={{ color: '#f89133' }}>Goo</span>
          </span>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          {loading ? (
            <p className="text-center text-gray-500 py-10">Chargement</p>
          ) : done ? (
            <div className="text-center py-6">
              <FaCheckCircle className="mx-auto w-12 h-12 text-green-500 mb-3" />
              <h2 className="text-lg font-bold text-gray-900">Rendez-vous confirmé</h2>
              <p className="mt-2 text-sm text-gray-600">
                Votre {type?.name || 'rendez-vous'} est réservé pour le{' '}
                <span className="font-semibold">
                  {fmtDayLabel(selectedDay)} à {fmtTime(selectedSlot)}
                </span>
                . Un membre de l'équipe OuiGoo vous contactera. Un email de confirmation vous a été envoyé si vous
                l'avez renseigné.
              </p>
            </div>
          ) : error && !slots.length ? (
            <p className="text-center text-red-600 py-10">{error}</p>
          ) : (
            <>
              <h1 className="text-xl font-bold text-gray-900">{type?.name || 'Prendre rendez-vous'}</h1>
              {type?.description && <p className="mt-1 text-sm text-gray-500">{type.description}</p>}
              <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-400">
                <FaRegClock className="w-3 h-3" /> {type?.durationMin || 30} min · fuseau {clientTz || 'local'}
              </p>

              {sortedDays.length === 0 ? (
                <p className="mt-6 text-center text-sm text-gray-500 py-8">
                  Aucun créneau disponible pour le moment. Réessayez plus tard.
                </p>
              ) : (
                <>
                  {/* Sélecteur de jour */}
                  <div className="mt-5 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const i = sortedDays.indexOf(selectedDay);
                        if (i > 0) {
                          setSelectedDay(sortedDays[i - 1]);
                          setSelectedSlot(null);
                        }
                      }}
                      disabled={sortedDays.indexOf(selectedDay) <= 0}
                      className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40"
                    >
                      <FaChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <div className="flex-1 text-center font-semibold capitalize text-gray-800">
                      {selectedDay ? fmtDayLabel(selectedDay) : ''}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const i = sortedDays.indexOf(selectedDay);
                        if (i < sortedDays.length - 1) {
                          setSelectedDay(sortedDays[i + 1]);
                          setSelectedSlot(null);
                        }
                      }}
                      disabled={sortedDays.indexOf(selectedDay) >= sortedDays.length - 1}
                      className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40"
                    >
                      <FaChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Créneaux du jour */}
                  <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {daySlots.map((s) => (
                      <button
                        key={s.startUtc}
                        type="button"
                        onClick={() => setSelectedSlot(s.startUtc)}
                        className={`rounded-xl border py-2.5 text-sm font-medium transition ${
                          selectedSlot === s.startUtc
                            ? 'border-transparent text-white'
                            : 'border-gray-200 text-gray-700 hover:border-orange-300'
                        }`}
                        style={selectedSlot === s.startUtc ? { backgroundColor: '#f89133' } : {}}
                      >
                        {fmtTime(s.startUtc)}
                      </button>
                    ))}
                  </div>

                  {/* Formulaire */}
                  {selectedSlot && (
                    <div className="mt-6 space-y-3 border-t border-gray-100 pt-5">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Field label="Nom complet *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                        <Field
                          label="Nom du business"
                          value={form.businessName}
                          onChange={(v) => setForm({ ...form, businessName: v })}
                        />
                        <Field
                          label="Téléphone"
                          value={form.phone}
                          onChange={(v) => setForm({ ...form, phone: v })}
                          type="tel"
                        />
                        <Field
                          label="Email"
                          value={form.email}
                          onChange={(v) => setForm({ ...form, email: v })}
                          type="email"
                        />
                        <Field label="Ville" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">Message (optionnel)</label>
                        <textarea
                          rows={2}
                          value={form.message}
                          onChange={(e) => setForm({ ...form, message: e.target.value })}
                          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
                        />
                      </div>

                      {error && <p className="text-sm text-red-600">{error}</p>}

                      <button
                        type="button"
                        onClick={submit}
                        disabled={submitting}
                        className="w-full rounded-xl py-3.5 text-white font-semibold transition disabled:opacity-50"
                        style={{ backgroundColor: '#f89133' }}
                      >
                        {submitting ? 'Envoi en cours' : `Confirmer pour ${fmtTime(selectedSlot)}`}
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
      />
    </div>
  );
}
